import {
  AriaProps,
  DataProps,
  DOMProps,
  LinkDOMProps,
  StyleProps,
} from '../../types';
import { stylePropKeys } from '../styleProps';
import {
  AriaPropKeys,
  ariaPropNames,
  DOMPropKeys,
  domPropNames,
  LinkPropKeys,
  linkPropNames,
} from '../domProps';

export const callbackPropNames = new Set(['onClick']);

const imagePropNames = new Set([
  'alt',
  'src',
  'srcSet',
  'sizes',
  'onLoad',
  'onError',
  'loading',
  'width',
  'height',
  'crossOrigin',
  'draggable',
]);

const textAreaPropNames = new Set([
  'value',
  'defaultValue',
  'placeholder',
  'name',
  'id',
  'rows',
  'cols',
  'wrap',
  'maxLength',
  'disabled',
  'readOnly',
  'required',
  'autoFocus',
  'spellCheck',
]);

interface Options {
  /**
   * Whether the element is a link and should include DOM props
   * for <a> elements.
   */
  isLink?: boolean;

  /**
   * Whether the element is an image and should include DOM props
   * for <img> elements.
   */
  isImage?: boolean;

  /**
   * Whether the element is an textArea and should include DOM props
   * for <textArea> elements.
   */
  isTextArea?: boolean;

  /**
   * The list of prop names that will always be filtered out.
   * Defaulted to `className` and `style`.
   */
  disallowedProps?: string[];
}

const defaultOpts: Options = {
  isLink: false,
  isImage: false,
  isTextArea: false,
  disallowedProps: ['className', 'style'],
};

const dataAttributePrefix = 'data-';

/**
 * Filters props passed to a component into the following categories:
 *
 * - Data props: custom data attributes.
 * - DOM props: data and aria attributes, role, id, etc.
 * - Token props: CSS-conformant token properties allowed by the design system.
 * - Callback props: Event handlers and other functions.
 */
export function filterProps(
  props: Record<string, any>,
  opts?: Options
): {
  dataProps: DataProps;
  domProps: DOMProps & AriaProps & DataProps & LinkDOMProps;
  tokenProps: StyleProps;
  callbackProps: Record<string, any>;
  restProps: Record<string, any>;
} {
  const options = { ...defaultOpts, ...opts };
  const { isLink, isImage, isTextArea, disallowedProps } = options;
  const disallowedPropNames = new Set(disallowedProps);

  const dataProps: Record<string, any> = {};
  const domProps: Record<string, any> = {};
  const tokenProps: Record<string, any> = {};
  const callbackProps: Record<string, any> = {};
  const restProps: Record<string, any> = {};

  Object.keys(props).forEach((key) => {
    // data attributes are not included in the if/else block because
    // we want them available in both the dataProps and domProps buckets
    if (key.startsWith(dataAttributePrefix)) {
      dataProps[key] = props[key];
    }

    if (
      domPropNames[key as DOMPropKeys] ||
      ariaPropNames[key as AriaPropKeys] ||
      (isLink && linkPropNames[key as LinkPropKeys]) ||
      (isImage && imagePropNames.has(key)) ||
      (isTextArea && textAreaPropNames.has(key)) ||
      key.startsWith(dataAttributePrefix)
    ) {
      domProps[key] = props[key];
    } else if (stylePropKeys[key as keyof typeof stylePropKeys]) {
      tokenProps[key] = props[key];
    } else if (callbackPropNames.has(key) || typeof props[key] === 'function') {
      callbackProps[key] = props[key];
    } else if (!disallowedPropNames.has(key)) {
      restProps[key] = props[key];
    }
  });

  return { dataProps, domProps, tokenProps, callbackProps, restProps };
}
